import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography
} from 'antd';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { publicStreamerApi, userApi } from 'src/api';
import { PAGE_KEYS } from 'src/constants/access';
import { setAppTitle } from 'src/store/app';

import './users.scss';

const PLATFORM_OPTIONS = [
  { label: 'Dashboard', value: 'dashboard' },
  { label: 'Desktop', value: 'desktop' },
  { label: 'Desktop Chaos', value: 'desktop-chaos' },
  { label: 'Overlay', value: 'overlay' }
];

const PAGE_OPTIONS = [
  { label: 'Dashboard', value: PAGE_KEYS.DASHBOARD },
  { label: 'Commands', value: PAGE_KEYS.COMMANDS },
  { label: 'Automode', value: PAGE_KEYS.AUTOMODE },
  { label: 'Chaos', value: PAGE_KEYS.CHAOS },
  { label: 'Chaos Widget', value: PAGE_KEYS.CHAOS_WIDGET },
  { label: 'Giveaway', value: PAGE_KEYS.GIVEAWAY },
  { label: 'Giveaway Embed', value: PAGE_KEYS.GIVEAWAY_EMBED },
  { label: 'Viewers', value: PAGE_KEYS.VIEWERS },
  { label: 'Profile', value: PAGE_KEYS.PROFILE },
  { label: 'Users', value: PAGE_KEYS.USERS }
];

const normalizePayload = (values) => ({
  login: String(values.login || '')
    .trim()
    .toLowerCase(),
  displayName: String(values.displayName || '').trim(),
  role: values.role || 'user',
  isActive: Boolean(values.isActive),
  platforms: Array.isArray(values.platforms) ? values.platforms : [],
  pages: Array.isArray(values.pages) ? values.pages : []
});

export const Users = () => {
  const { t } = useTranslation('users');
  const { t: tPublic } = useTranslation('users_public');
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const [list, setList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [publicStreamers, setPublicStreamers] = useState([]);
  const [isPublicStreamersLoading, setIsPublicStreamersLoading] = useState(false);
  const [isSyncingPublicStreamers, setIsSyncingPublicStreamers] = useState(false);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await userApi.getUsers();
      setList(Array.isArray(response?.data?.data) ? response.data.data : []);
    } catch (error) {
      const message = error?.response?.data?.message || t('errors.load_failed');
      toast(message, { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    dispatch(setAppTitle(t('menu:users')));
  }, [dispatch, t]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const loadPublicStreamers = useCallback(async () => {
    setIsPublicStreamersLoading(true);
    try {
      const response = await publicStreamerApi.getAdminPublicStreamers();
      setPublicStreamers(Array.isArray(response?.data?.data) ? response.data.data : []);
    } catch (error) {
      toast(error?.response?.data?.message || tPublic('errors.load_failed'), { type: 'error' });
    } finally {
      setIsPublicStreamersLoading(false);
    }
  }, [tPublic]);

  useEffect(() => {
    loadPublicStreamers();
  }, [loadPublicStreamers]);

  const openCreateModal = useCallback(() => {
    setEditingUser(null);
    form.setFieldsValue({
      login: '',
      displayName: '',
      role: 'user',
      isActive: true,
      platforms: ['dashboard'],
      pages: [PAGE_KEYS.DASHBOARD]
    });
    setIsModalOpen(true);
  }, [form]);

  const openEditModal = useCallback(
    (user) => {
      setEditingUser(user);
      form.setFieldsValue({
        login: user?.login || '',
        displayName: user?.displayName || '',
        role: user?.role || 'user',
        isActive: user?.isActive !== false,
        platforms: Array.isArray(user?.platforms) ? user.platforms : [],
        pages: Array.isArray(user?.pages) ? user.pages : []
      });
      setIsModalOpen(true);
    },
    [form]
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingUser(null);
    form.resetFields();
  }, [form]);

  const onSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const payload = normalizePayload(values);

      setIsSubmitting(true);

      if (editingUser?._id) {
        await userApi.updateUser(editingUser._id, payload);
        toast(t('messages.updated'), { type: 'success' });
      } else {
        await userApi.createUser(payload);
        toast(t('messages.created'), { type: 'success' });
      }

      closeModal();
      await loadUsers();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }

      const message =
        error?.response?.data?.error || error?.response?.data?.message || t('errors.save_failed');
      toast(message, { type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  }, [closeModal, editingUser, form, loadUsers, t]);

  const onDeleteUser = useCallback(
    async (userId) => {
      try {
        await userApi.deleteUser(userId);
        toast(t('messages.deleted'), { type: 'success' });
        await loadUsers();
      } catch (error) {
        const message =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          t('errors.delete_failed');
        toast(message, { type: 'error' });
      }
    },
    [loadUsers, t]
  );

  const syncPublicStreamers = useCallback(async () => {
    setIsSyncingPublicStreamers(true);
    try {
      const response = await publicStreamerApi.syncPublicStreamers();
      toast(tPublic('messages.synced', { count: response?.data?.streamers || 0 }), { type: 'success' });
      await loadPublicStreamers();
    } catch (error) {
      toast(error?.response?.data?.message || tPublic('errors.sync_failed'), { type: 'error' });
    } finally {
      setIsSyncingPublicStreamers(false);
    }
  }, [loadPublicStreamers, tPublic]);

  const columns = useMemo(
    () => [
      {
        title: t('table.login'),
        dataIndex: 'login',
        key: 'login',
        width: 180,
        render: (value, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>{value}</Typography.Text>
            {row?.displayName ? (
              <Typography.Text type="secondary">{row.displayName}</Typography.Text>
            ) : null}
          </Space>
        )
      },
      {
        title: t('table.role'),
        dataIndex: 'role',
        key: 'role',
        width: 120,
        render: (value) => (
          <Tag color={value === 'admin' ? 'gold' : 'default'}>{value || 'user'}</Tag>
        )
      },
      {
        title: t('table.active'),
        dataIndex: 'isActive',
        key: 'isActive',
        width: 110,
        render: (value) =>
          value ? <Tag color="green">{t('labels.active')}</Tag> : <Tag>{t('labels.inactive')}</Tag>
      },
      {
        title: t('table.platforms'),
        dataIndex: 'platforms',
        key: 'platforms',
        render: (platforms) => (
          <Space size={[4, 4]} wrap>
            {(Array.isArray(platforms) ? platforms : []).map((platform) => (
              <Tag key={platform} color="blue">
                {platform}
              </Tag>
            ))}
          </Space>
        )
      },
      {
        title: t('table.pages'),
        dataIndex: 'pages',
        key: 'pages',
        render: (pages) => (
          <Space size={[4, 4]} wrap>
            {(Array.isArray(pages) ? pages : []).map((page) => (
              <Tag key={page} color="purple">
                {page}
              </Tag>
            ))}
          </Space>
        )
      },
      {
        title: t('table.actions'),
        key: 'actions',
        width: 180,
        render: (_, row) => (
          <Space>
            <Button size="small" onClick={() => openEditModal(row)}>
              {t('actions.edit')}
            </Button>
            <Popconfirm
              title={t('actions.confirm_delete')}
              okText={t('actions.delete')}
              cancelText={t('actions.cancel')}
              onConfirm={() => onDeleteUser(row._id)}
            >
              <Button size="small" danger>
                {t('actions.delete')}
              </Button>
            </Popconfirm>
          </Space>
        )
      }
    ],
    [onDeleteUser, openEditModal, t]
  );

  return (
    <div className="users-page">
      <div className="users-page__content">
        <div className="users-page__header">
          <Button loading={isSyncingPublicStreamers} onClick={syncPublicStreamers}>
            {tPublic('actions.sync')}
          </Button>
          <Button type="primary" onClick={openCreateModal}>
            {t('actions.add_user')}
          </Button>
        </div>

        <Card className="users-page__card" bordered={false}>
          <Table
            rowKey="_id"
            loading={isLoading}
            columns={columns}
            dataSource={list}
            pagination={false}
          />
        </Card>

        <Card className="users-page__card" title={tPublic('title')} bordered={false}>
          <Table
            rowKey="twitchUrl"
            loading={isPublicStreamersLoading}
            dataSource={publicStreamers}
            pagination={false}
            columns={[
              {
                title: tPublic('table.streamer'),
                key: 'streamer',
                render: (_, row) => (
                  <Space>
                    {row.profileImageUrl ? <img className="users-page__avatar" src={row.profileImageUrl} alt="" /> : null}
                    <Typography.Text>{row.displayName || tPublic('labels.unnamed')}</Typography.Text>
                  </Space>
                )
              },
              {
                title: 'Twitch',
                dataIndex: 'twitchUrl',
                key: 'twitchUrl',
                render: (url) => <a href={url} target="_blank" rel="noopener noreferrer">Twitch</a>
              }
            ]}
          />
        </Card>
      </div>

      <Modal
        open={isModalOpen}
        title={editingUser ? t('modal.edit_title') : t('modal.create_title')}
        onCancel={closeModal}
        onOk={onSubmit}
        confirmLoading={isSubmitting}
        okText={t('actions.save')}
        cancelText={t('actions.cancel')}
      >
        <Form form={form} layout="vertical" initialValues={{ role: 'user', isActive: true }}>
          <Form.Item
            label={t('fields.login')}
            name="login"
            rules={[{ required: true, message: t('errors.login_required') }]}
          >
            <Input placeholder="twitch_login" autoComplete="off" />
          </Form.Item>

          <Form.Item label={t('fields.display_name')} name="displayName">
            <Input placeholder={t('fields.display_name')} autoComplete="off" />
          </Form.Item>

          <Form.Item label={t('fields.role')} name="role">
            <Select
              options={[
                { value: 'user', label: 'user' },
                { value: 'admin', label: 'admin' }
              ]}
            />
          </Form.Item>

          <Form.Item label={t('fields.active')} name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item label={t('fields.platforms')} name="platforms">
            <Checkbox.Group options={PLATFORM_OPTIONS} />
          </Form.Item>

          <Form.Item label={t('fields.pages')} name="pages">
            <Checkbox.Group options={PAGE_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>

    </div>
  );
};
